import React, { useEffect, useState } from 'react';
import ModalDialog from '../components/modal/ModalDialog';
import './ConfirmOptionsModal.scss';
import { Button, ButtonSize, ButtonTheme } from './Button';
import { Trans, useTranslation } from 'react-i18next';
import HorizontalRuler from './HorizontalRuler';
import { PriceSelector, PriceSelectorHelpTooltip } from '../search/PriceSelector';
import { PriceTier } from '../../../models/PriceTier';
import { SubscriptionPeriodType } from '../../../models/Article';
import { RadioButton, RadioGroup } from './RadioGroup';
import styled from 'styled-components';
import { useSubscriptionPeriods } from '../../api/subscriptions/useSubscriptionPeriods';

export type ConfirmOptionsModalProps<T> = {
    readonly options: T | undefined;
    readonly changePrice: (
        options: T & { priceTier: PriceTier; subscriptionPeriodType: SubscriptionPeriodType }
    ) => Promise<void>;
    readonly showUsageConditionOption: boolean;
    readonly showSubscriptionStartDateOption: boolean;
    readonly closeModal: () => void;
};

const StRadioButtonLabel = styled.span`
    margin-left: 10px;
`;
const StRadioButtonDateLabel = styled.span`
    color: #979797;
`;

type Options = {
    readonly priceTier?: PriceTier;
    readonly subscriptionPeriodType?: SubscriptionPeriodType;
};

export const DEFAULT_PRICE_TIER: PriceTier = { lendingModel: 'OCOU', level: 'MIN' };
export const DEFAULT_SUBSCRIPTION_PERIOD_TYPE = SubscriptionPeriodType.CURRENT;

export function ConfirmOptionsModal<T extends Options>({
    options,
    showUsageConditionOption,
    showSubscriptionStartDateOption,
    closeModal,
    changePrice
}: ConfirmOptionsModalProps<T>) {
    const HEIGHT_SPACER = 5;
    const { t } = useTranslation();
    const { data: subscriptionPeriodData } = useSubscriptionPeriods();
    const [selectedPriceTier, setSelectedPriceTier] = useState<PriceTier>(options?.priceTier ?? DEFAULT_PRICE_TIER);
    const [selectedSubscriptionPeriodType, setSelectedSubscriptionPeriodType] = useState<SubscriptionPeriodType>(
        options?.subscriptionPeriodType ?? DEFAULT_SUBSCRIPTION_PERIOD_TYPE
    );
    const [isLoading, setIsLoading] = useState(false);

    const onCloseModal = () => {
        setSelectedPriceTier(options?.priceTier ?? DEFAULT_PRICE_TIER);
        setSelectedSubscriptionPeriodType(options?.subscriptionPeriodType ?? DEFAULT_SUBSCRIPTION_PERIOD_TYPE);
        closeModal();
    };

    useEffect(() => {
        setSelectedPriceTier(options?.priceTier ?? DEFAULT_PRICE_TIER);
    }, [options?.priceTier]);

    useEffect(() => {
        setSelectedSubscriptionPeriodType(options?.subscriptionPeriodType ?? DEFAULT_SUBSCRIPTION_PERIOD_TYPE);
    }, [options?.subscriptionPeriodType]);

    if (!options) {
        return null;
    }

    const onProceed = async () => {
        setIsLoading(true);
        try {
            await changePrice({
                ...options,
                priceTier: selectedPriceTier,
                subscriptionPeriodType: selectedSubscriptionPeriodType
            });
        } finally {
            setIsLoading(false);
            onCloseModal();
        }
    };

    return (
        <ModalDialog
            show={true}
            title={t('components.confirmOptionsModal.title')}
            titleClassName="title"
            wrapClassName="confirm-options-modal"
            boldHeader={false}
        >
            {showUsageConditionOption ? (
                <>
                    <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
                    <div className="subtitle">
                        <span>{t('components.confirmOptionsModal.selectUsageConditionOption')}</span>
                        <PriceSelectorHelpTooltip className="modal-tooltip" />
                    </div>
                    <div className="price-selector">
                        <PriceSelector value={selectedPriceTier} onChange={setSelectedPriceTier} />
                    </div>
                    <br />
                </>
            ) : null}
            {showSubscriptionStartDateOption && subscriptionPeriodData ? (
                <>
                    <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
                    <div className="subtitle">{t('components.confirmOptionsModal.selectSubscriptionStartDate')}</div>
                    <p className="description">
                        <Trans
                            i18nKey="components.confirmOptionsModal.subscriptionText"
                            components={[<b />]}
                            values={{
                                renewalDate: t('dateFormat.dateWithoutYear', {
                                    val: new Date(subscriptionPeriodData.nextSubscriptionPeriod.startDate)
                                })
                            }}
                        />
                    </p>
                    <RadioGroup
                        isInline={false}
                        value={selectedSubscriptionPeriodType}
                        onChange={newPeriod => setSelectedSubscriptionPeriodType(newPeriod as SubscriptionPeriodType)}
                        className="add-to-selector-buttons"
                    >
                        <RadioButton value={SubscriptionPeriodType.CURRENT}>
                            <StRadioButtonLabel>
                                {t('components.subscriptionPeriodPopup.immediately')}
                            </StRadioButtonLabel>
                            <StRadioButtonDateLabel>
                                {' '}
                                {t('components.subscriptionPeriodPopup.startAndEndDate', {
                                    startDate: t('dateFormat.dateWithShortMonth', {
                                        val: new Date(subscriptionPeriodData.currentSubscriptionPeriod.startDate)
                                    }),
                                    endDate: t('dateFormat.dateWithShortMonth', {
                                        val: new Date(subscriptionPeriodData.currentSubscriptionPeriod.endDate)
                                    }),
                                    count: subscriptionPeriodData.currentSubscriptionPeriod.lengthInDays
                                })}
                            </StRadioButtonDateLabel>
                        </RadioButton>
                        <RadioButton value={SubscriptionPeriodType.NEXT}>
                            <StRadioButtonLabel>
                                {t('components.subscriptionPeriodPopup.nextSubscriptionYear')}
                            </StRadioButtonLabel>
                            <StRadioButtonDateLabel>
                                {' '}
                                {t('components.subscriptionPeriodPopup.startAndEndDate', {
                                    startDate: t('dateFormat.dateWithShortMonth', {
                                        val: new Date(subscriptionPeriodData.nextSubscriptionPeriod.startDate)
                                    }),
                                    endDate: t('dateFormat.dateWithShortMonth', {
                                        val: new Date(subscriptionPeriodData.nextSubscriptionPeriod.endDate)
                                    }),
                                    count: subscriptionPeriodData.nextSubscriptionPeriod.lengthInDays
                                })}
                            </StRadioButtonDateLabel>
                        </RadioButton>
                    </RadioGroup>
                </>
            ) : null}
            <HorizontalRuler heightSpacer={HEIGHT_SPACER} />
            <br />
            <div className="actions">
                <Button
                    onClick={onCloseModal}
                    text={t('cancel')}
                    theme={ButtonTheme.POSITIVE_INVERT}
                    size={ButtonSize.LARGE}
                    className="cancel-button"
                    disabled={isLoading}
                />
                <Button
                    onClick={onProceed}
                    text={t('confirm')}
                    className="proceed-button"
                    data-testid="confirm-options-proceed"
                    size={ButtonSize.LARGE}
                    disabled={isLoading}
                    isLoading={isLoading}
                />
            </div>
        </ModalDialog>
    );
}
